import React, { useEffect, useState } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import rtlPlugin from 'stylis-plugin-rtl';
import { prefixer } from 'stylis';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { CssVarsProvider, extendTheme } from '@mui/joy/styles';
import { ErrorBoundary } from 'react-error-boundary';
import { Button, Typography, Stack } from '@mui/joy';
import { routes } from './routes/routeConfig';
import Navbar from './components/sections/Navbar';
import useAppStore from './store/appStore';
import LoadingSpinner from './components/ui/Loading';
import PageWrapper from './components/PageWrapper';

function ErrorFallback({ error, resetErrorBoundary }) {
  const navigate = useNavigate();

  const handleGoBack = () => {
    resetErrorBoundary();
    navigate(-1);
  };

  return (
    <div className="flex items-center justify-center min-h-screen w-full p-4">
      <Stack
        spacing={2}
        alignItems="center"
        sx={{
          maxWidth: '90vw',
          textAlign: 'center',
        }}
      >
        <Typography
          level="h2"
          sx={{
            fontSize: {
              xs: '1.5rem',
              sm: '2rem',
              md: '2.5rem',
              lg: '3rem',
            },
            textAlign: 'center',
          }}
        >
          Oops! Something went wrong
        </Typography>
        {process.env.NODE_ENV === 'development' && (
          <div className="w-full max-w-screen p-4 bg-red-100 text-red-800 overflow-x-auto">
            <Typography level="body-md" sx={{ textAlign: 'center' }}>
              {error.message}
            </Typography>
          </div>
        )}
        <Button variant="soft" color="primary" onClick={handleGoBack}>
          Go Back
        </Button>
      </Stack>
    </div>
  );
}

function App() {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const [nextPage, setNextPage] = useState({});

  useEffect(() => {
    navigate(nextPage.route, nextPage.options);
  }, [nextPage]);

  const { loading, isRTL, setIsRTL } = useAppStore();

  const theme = extendTheme({
    direction: isRTL ? 'rtl' : 'ltr',
  });

  const cacheRtl = createCache({
    key: 'joyrtl',
    stylisPlugins: [prefixer, rtlPlugin],
  });

  const cacheLtr = createCache({
    key: 'joyltr',
    stylisPlugins: [prefixer],
  });

  useEffect(() => {
    const isRTL = i18n.language === 'ar';
    setIsRTL(isRTL);
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  return (
    <CssVarsProvider theme={theme}>
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        <CacheProvider value={isRTL ? cacheRtl : cacheLtr}>
          <div dir={isRTL ? 'rtl' : 'ltr'}>
            {loading && <LoadingSpinner />}
            <Navbar />
            <Routes>
              {Object.values(routes).map(
                ({ path, component: Component, accessLevel }) => (
                  <Route
                    key={path}
                    path={path}
                    element={
                      path === '/' ? (
                        <Component />
                      ) : (
                        <PageWrapper
                          referer={path}
                          accessLevel={accessLevel}
                          nextPage={nextPage}
                        >
                          <Component setNextPage={setNextPage} />
                        </PageWrapper>
                      )
                    }
                  />
                )
              )}
            </Routes>
          </div>
        </CacheProvider>
      </ErrorBoundary>
    </CssVarsProvider>
  );
}

export default App;
